import React, { useMemo, useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import useThemeColors from "../organization/portal/useThemeColors";
import useTranslation from "../../i18n/useTranslation";
import {BULK_EDIT_EVENTS} from "../stages/Fixtures";
import {useMutation} from "@apollo/client";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

const EMPTY_LIST = [];

export default function BulkEditGamesModal({
                                               modalId = "bulk-edit-games-modal",
                                               selectedEvents = [],
                                               referees = [],
                                               managers = [],
                                               stages = [],
                                               onResolveAllMatchingEventIds = async () => [],
                                               onUpdated = () => {},
                                           }) {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const safeSelectedEvents = Array.isArray(selectedEvents) ? selectedEvents : EMPTY_LIST;
    const safeStages = Array.isArray(stages) ? stages : EMPTY_LIST;
    const safeManagers = Array.isArray(managers) ? managers : EMPTY_LIST;

    const [bulkEditGames] = useMutation(BULK_EDIT_EVENTS);

    const [fieldId, setFieldId] = useState("");
    const [refereeIds, setRefereeIds] = useState([]);
    const [managerIds, setManagerIds] = useState([]);
    const [description, setDescription] = useState("");
    const [refereesTouched, setRefereesTouched] = useState(false);
    const [managersTouched, setManagersTouched] = useState(false);
    const [descriptionTouched, setDescriptionTouched] = useState(false);
    const [applyToAllMatching, setApplyToAllMatching] = useState(false);

    const [saving, setSaving] = useState(false);

    const eventIds = useMemo(() => safeSelectedEvents.map((event) => event.id), [safeSelectedEvents]);

    const availableFields = useMemo(() => {
        const selectedStageIds = new Set(
            safeSelectedEvents.map((event) => event?.stage?.id).filter(Boolean)
        );

        const map = new Map();
        safeStages.forEach((stage) => {
            if (!selectedStageIds.has(stage?.id)) return;
            (stage?.fields || []).forEach((field) => {
                map.set(field.id, field);
            });
        });

        return Array.from(map.values());
    }, [safeSelectedEvents, safeStages]);

    const availableReferees = useMemo(() => {
        const stagePools = safeSelectedEvents.map((event) => {
            const stage = safeStages.find((item) => String(item.id) === String(event?.stage?.id));
            return event?.stage?.stageReferees || stage?.stageReferees || [];
        });
        if (!stagePools.length) return [];

        const poolsById = stagePools.map((pool) => new Map(pool.map((referee) => [String(referee.id), referee])));
        return Array.from(poolsById[0].entries())
            .filter(([id]) => poolsById.every((pool) => pool.has(id)))
            .map(([, referee]) => referee);
    }, [safeSelectedEvents, safeStages]);

    const resetForm = useCallback(() => {
        setFieldId("");
        setRefereeIds([]);
        setManagerIds([]);
        setDescription("");
        setRefereesTouched(false);
        setManagersTouched(false);
        setDescriptionTouched(false);
        setApplyToAllMatching(false);
    }, []);

    useEffect(() => {
        resetForm();
    }, [safeSelectedEvents, resetForm]);

    const handleSubmit = useCallback(
        async (e) => {
            e.preventDefault();

            let targetEventIds = eventIds;

            if (applyToAllMatching) {
                targetEventIds = await onResolveAllMatchingEventIds();
            }

            if (!targetEventIds.length) {
                toast.info(t("bulkEditGamesModal.noEventsSelected"));
                return;
            }

            if (!fieldId && !refereesTouched && !managersTouched && !descriptionTouched) {
                toast.info(t("bulkEditGamesModal.validation.selectAtLeastOneChange"));
                return;
            }

            setSaving(true);

            try {
                const variables = {
                    eventIds: targetEventIds,
                };

                if (fieldId) variables.fieldId = parseInt(fieldId, 10);
                if (refereesTouched) {
                    variables.refereeIds = refereeIds
                        .map((id) => parseInt(id, 10))
                        .filter((id) => Number.isFinite(id));
                }
                if (managersTouched) {
                    variables.managerIds = managerIds
                        .map((id) => parseInt(id, 10))
                        .filter((id) => Number.isFinite(id));
                }
                if (descriptionTouched) variables.description = description.trim();

                const res = await bulkEditGames({ variables });

                if (res.data?.bulkEditEvents?.success) {
                    toast.success(
                        t("bulkEditGamesModal.success", {
                            count: targetEventIds.length,
                        }),
                        {
                            autoClose: 2500,
                        }
                    );

                    onUpdated();

                    if (window.$ && window.$(`#${modalId}`)?.modal) {
                        window.$(`#${modalId}`).modal("hide");
                    }
                } else {
                    toast.error(t("bulkEditGamesModal.failed"));
                }
            } catch (err) {
                toast.error(getLocalizedGraphQLError(err, t, "bulkEditGamesModal.failed"));
            } finally {
                setSaving(false);
            }
        },
        [
            bulkEditGames,
            description,
            eventIds,
            fieldId,
            managersTouched,
            managerIds,
            modalId,
            onUpdated,
            refereesTouched,
            refereeIds,
            descriptionTouched,
            applyToAllMatching,
            onResolveAllMatchingEventIds,
            t,
        ]
    );

    return (
        <div
            className="modal fade"
            id={modalId}
            tabIndex="-1"
            role="dialog"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-lg" role="document">
                <div className="modal-content kbola-modalContent">
                    <form onSubmit={handleSubmit}>
                        <div className="modal-header kbola-modalHeader">
                            <h5 className="modal-title">
                                {t("bulkEditGamesModal.title")}
                            </h5>

                            <button
                                type="button"
                                className="close"
                                data-dismiss="modal"
                            >
                                <span>&times;</span>
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="alert alert-info">
                                {t("bulkEditGamesModal.selectedCount", {
                                    count: safeSelectedEvents.length,
                                })}
                            </div>

                            <div className="form-group">
                                <div className="custom-control custom-checkbox">
                                    <input
                                        id={`${modalId}-all-matching`}
                                        type="checkbox"
                                        className="custom-control-input"
                                        checked={applyToAllMatching}
                                        onChange={(e) => setApplyToAllMatching(e.target.checked)}
                                    />
                                    <label
                                        className="custom-control-label"
                                        htmlFor={`${modalId}-all-matching`}
                                    >
                                        {t("bulkEditGamesModal.applyToAllMatching")}
                                    </label>
                                </div>
                                <small className="form-text text-muted">
                                    {t("bulkEditGamesModal.applyToAllMatchingHint")}
                                </small>
                            </div>

                            {/* FIELD */}
                            <div className="form-group">
                                <label>{t("bulkEditGamesModal.field")}</label>
                                <select
                                    className="form-control"
                                    value={fieldId}
                                    onChange={(e) => setFieldId(e.target.value)}
                                >
                                    <option value="">
                                        {t("bulkEditGamesModal.noChange")}
                                    </option>

                                    {availableFields.map((f) => (
                                        <option key={f.id} value={f.id}>
                                            {f.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* REFEREES */}
                            <div className="form-group">
                                <label>{t("bulkEditGamesModal.referees")}</label>
                                <select
                                    className="form-control"
                                    multiple
                                    value={refereeIds}
                                    onChange={(e) => {
                                        setRefereesTouched(true);
                                        setRefereeIds(
                                            Array.from(
                                                e.target.selectedOptions
                                            ).map((o) => o.value)
                                        );
                                    }}
                                >
                                    {availableReferees.map((r) => (
                                        <option key={r.id} value={r.id}>
                                            {r.fullName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* MANAGERS */}
                            <div className="form-group">
                                <label>{t("bulkEditGamesModal.managers")}</label>
                                <select
                                    className="form-control"
                                    multiple
                                    value={managerIds}
                                    onChange={(e) => {
                                        setManagersTouched(true);
                                        setManagerIds(
                                            Array.from(
                                                e.target.selectedOptions
                                            ).map((o) => o.value)
                                        );
                                    }}
                                >
                                    {safeManagers.map((m) => (
                                        <option key={m.id} value={m.id}>
                                            {m.fullName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* DESCRIPTION */}
                            <div className="form-group">
                                <label>{t("bulkEditGamesModal.description")}</label>
                                <input
                                    className="form-control"
                                    type="text"
                                    value={description}
                                    onChange={(e) => {
                                        setDescriptionTouched(true);
                                        setDescription(e.target.value);
                                    }}
                                    placeholder={t(
                                        "bulkEditGamesModal.descriptionPlaceholder"
                                    )}
                                />
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-default"
                                data-dismiss="modal"
                            >
                                {t("common.cancel")}
                            </button>

                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={saving}
                            >
                                {saving
                                    ? t("common.saving")
                                    : t("bulkEditGamesModal.save")}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Styling aligned with your system */}
            <style>{`
                .kbola-modalContent {
                    border-radius: 16px;
                    overflow: hidden;
                    border: 1px solid rgba(0,0,0,0.08);
                }

                .kbola-modalHeader {
                    background: ${colors.primary};
                    color: ${colors.textOnPrimary};
                    font-weight: 900;
                }

                .modal-body label {
                    font-weight: 800;
                    font-size: 13px;
                    color: rgba(20,21,26,0.75);
                }

                .form-control {
                    border-radius: 10px;
                    font-weight: 700;
                }

                .form-control:focus {
                    box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.2);
                    border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.6);
                }
            `}</style>
        </div>
    );
}
